// DEP
import { saveAs } from 'file-saver';

// ENVIRONMENT
import { environment } from '../../environments/environment';

// ANGULAR
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS 6
import { Observable, of, BehaviorSubject, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

// MODELS
import {
  initHttpParams,
  IWHWorkObjectEndpointConfigModel,
  IWHWorkObjectPreviewDTO,
  WHWorkObjectTypeENUM,
  IWHEquipmentDTO,
  WHEquipmentDOM,
  IWHEquipmentPreviewDTO,
  FLOW_GATEWAY,
  Pageable,
  WHFileUploadDataService,
} from '@workheld/workheld-shared-lib';

export interface EquipmentPlannerWorkObject {
  confirmed: boolean;
  customerId: string;
  endDate: string;
  executionGroupId: string;
  extId: string;
  fixed: boolean;
  id: string;
  workObjectId: string;
  projectTitle: string;
  startDate: string;
  stopped: boolean;
  title: string;
  workObjectTypeId: WHWorkObjectTypeENUM;
  teamId: string;
  workSteps: { startDate: string; endDate: string }[];
}

interface SimpleEquipment {
  bomNodeId: string;
  equipmentNumber: string;
  id: string;
  name: string;
}

export interface SimpleEquipmentPageable extends Pageable {
  content: SimpleEquipment[];
}

class DecommissionEntity {
  _showDecommissioned: BehaviorSubject<boolean>;
  showDecommissioned$: Observable<boolean>;

  constructor() {
    this._showDecommissioned = new BehaviorSubject<boolean>(false);
    this.showDecommissioned$ = this._showDecommissioned.asObservable();
  }
}

@Injectable()
export class EquipmentAsyncService {
  public equipmentData = new DecommissionEntity();

  private _equipmentDropdownList = new BehaviorSubject<IWHEquipmentPreviewDTO>(
    null,
  );
  private _equipmentSimpleDropdownList =
    new BehaviorSubject<SimpleEquipmentPageable>(null);
  equipmentDropdown = {
    equipmentDropdownList$: this._equipmentDropdownList.asObservable(),
    equipmentSimpleDropdownList$:
      this._equipmentSimpleDropdownList.asObservable(),
    equipmentDropdownloading$: new BehaviorSubject(true),
  };

  constructor(
    private httpClient: HttpClient,
    private fileUploadDataService: WHFileUploadDataService,
  ) {}

  public removeImageFile(equipmentId: string): Observable<any> {
    return this.httpClient.delete<any>(
      `${
        environment.apiUrl + FLOW_GATEWAY
      }/equipments/${equipmentId}/equipmentPicture`,
    );
  }

  public caseQuickCreate(payload: {
    equipmentId: string;
    category: string;
  }): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.apiUrl + FLOW_GATEWAY}/cases/category`,
      payload,
    );
  }

  public getEquipmentDtoByEquimentId(
    equipmentID: string,
  ): Observable<IWHEquipmentDTO> {
    return this.httpClient.get<IWHEquipmentDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/equipments/${equipmentID}`,
    );
  }

  public getEquipmentDtoByBomNodeId(
    bomNodeId: string,
  ): Observable<IWHEquipmentDTO> {
    return this.httpClient.get<IWHEquipmentDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/equipments/bomnode/${bomNodeId}`,
    );
  }

  public createEquipmentByEquipmentDTO(
    equipmentDTO: IWHEquipmentDTO,
  ): Observable<IWHEquipmentDTO> {
    return this.httpClient.post<IWHEquipmentDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/equipments`,
      equipmentDTO,
    );
  }

  public updateEquipmentByEquipmentDTO(
    equipmentID: string,
    equipmentDTO: IWHEquipmentDTO,
  ): Observable<IWHEquipmentDTO> {
    return this.httpClient.post<IWHEquipmentDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/equipments/${equipmentID}`,
      equipmentDTO,
    );
  }

  public postEquipmentImageFile(
    equipmentID: string,
    fileBlob: Blob,
  ): Observable<IWHEquipmentDTO> {
    const fileType = fileBlob.type || '';
    const mimeType = fileType || 'application/octet-stream';
    const extension = fileType.startsWith('image/')
      ? `.${fileType.split('/')[1]}`
      : '';

    return this.fileUploadDataService
      .uploadFile(
        {
          apiUrl: environment.apiUrl + FLOW_GATEWAY,
        },
        {
          targetType: 'BOM_NODE_PICTURE',
          targetId: equipmentID,
        },
        fileBlob,
        {
          fileName: `avatar${extension}`,
          mimeType,
        },
      )
      .pipe(switchMap(() => this.getEquipmentDtoByEquimentId(equipmentID)));
  }

  // // TODO - check - with caching
  public getEquipmentQrCodeImage(equipmentQrCodeUrl: string): Observable<Blob> {
    return this.httpClient.get(equipmentQrCodeUrl, { responseType: 'blob' });
  }

  // TODO - check - without caching
  public loadQRCode(equipmentQrCodeUrl: string): Observable<Blob> {
    return this.httpClient.get(equipmentQrCodeUrl, {
      headers: { Accept: 'image/png' },
      responseType: 'blob',
    });
  }

  // QR CODE
  public saveQRCode(equipmentDOM: WHEquipmentDOM, qrBlob: Blob) {
    saveAs(
      qrBlob,
      'qr-' + equipmentDOM.name + '_' + equipmentDOM.equipmentNumber + '.png',
    );
  }

  public getEquipmentsPreview(
    httpParams: HttpParams,
  ): Observable<IWHEquipmentPreviewDTO> {
    return this.httpClient.get<IWHEquipmentPreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/equipments/preview`,
      { params: httpParams },
    );
  }

  public equipmentPlannerWorkObjects$ = new BehaviorSubject<
    EquipmentPlannerWorkObject[]
  >(null);
  public equipmentPlannerWorkObjectsLoading$ = new BehaviorSubject(null);
  private equipmentPlannerWorkObjectsRequest: Subscription | undefined;

  public getEquipmentWorkObjects(
    id: string,
    params?: {
      startDate?: string;
      endDate?: string;
      includeBomNodes?: boolean;
      isWorkOrderView: boolean;
      teamId?: string;
      searchTerm?: string;
    },
  ) {
    this.equipmentPlannerWorkObjectsRequest?.unsubscribe();
    this.equipmentPlannerWorkObjectsLoading$.next(true);
    const { teamId, searchTerm, ...backendParams } = params ?? {};
    const httpParams: HttpParams = initHttpParams(backendParams);
    this.equipmentPlannerWorkObjectsRequest = this.httpClient
      .get(
        environment.apiUrl + FLOW_GATEWAY + `/equipments/${id}/workobjects`,
        { params: httpParams },
      )
      .subscribe({
        next: (res: any) => {
          this.equipmentPlannerWorkObjectsLoading$.next(false);
          this.equipmentPlannerWorkObjects$.next(res);
        },
        error: () => {
          this.equipmentPlannerWorkObjectsLoading$.next(false);
          this.equipmentPlannerWorkObjects$.next([]);
        },
      });

    return this.equipmentPlannerWorkObjectsRequest;
  }

  public getWorkObjectPreview(
    context: 'EQUIPMENT' | 'BOMNODE',
    endpointConfig: IWHWorkObjectEndpointConfigModel,
    httpParams: HttpParams,
  ): Observable<IWHWorkObjectPreviewDTO> {
    if (context === 'EQUIPMENT') {
      return this.httpClient.get<IWHWorkObjectPreviewDTO>(
        `${endpointConfig.apiUrl}/equipments/${endpointConfig.equipmentId}/workobjects/preview`,
        { params: httpParams },
      );
    }
    return this.httpClient.get<IWHWorkObjectPreviewDTO>(
      `${endpointConfig.apiUrl}/bomnode/${endpointConfig.bomNodeId}/workobjects/preview`,
      { params: httpParams },
    );
  }

  public getEquipmentList(
    customerId: string,
    queryParams: {
      page: number;
      pageSize: number;
      term: string;
      sort?: string;
      sortMode?: 'ASC' | 'DESC';
    },
  ): Observable<IWHEquipmentPreviewDTO> {
    let httpParams: HttpParams = initHttpParams(queryParams);
    return this.getCustomerEquipmentPreview(customerId, httpParams);
  }

  public getCustomerEquipmentPreview(
    customerId: string,
    httpParams: HttpParams,
  ): Observable<IWHEquipmentPreviewDTO> {
    return this.httpClient.get<IWHEquipmentPreviewDTO>(
      `${
        environment.apiUrl + FLOW_GATEWAY
      }/customers/${customerId}/equipments/preview`,
      { params: httpParams },
    );
  }

  public getEquipmentDropdownList(httpParams) {
    let params = initHttpParams(httpParams);
    this.equipmentDropdown.equipmentDropdownloading$.next(true);
    return this.httpClient
      .get<IWHEquipmentPreviewDTO>(
        `${environment.apiUrl + FLOW_GATEWAY}/equipments/list`,
        { params },
      )
      .pipe(
        map((resp) => {
          const modifiedResp = { ...resp, data: resp?.equipments || [] };
          this.equipmentDropdown.equipmentDropdownloading$.next(false);
          this._equipmentDropdownList.next(modifiedResp);
        }),
        catchError((error) => {
          this.equipmentDropdown.equipmentDropdownloading$.next(false);
          return of(error);
        }),
      )
      .subscribe();
  }

  public getEquipmentSimpleList(httpParams) {
    let params = initHttpParams(httpParams);

    this.equipmentDropdown.equipmentDropdownloading$.next(true);

    return this.httpClient
      .get<SimpleEquipmentPageable>(
        `${environment.apiUrl + FLOW_GATEWAY}/equipments/simple`,
        { params },
      )
      .pipe(
        map((resp) => {
          this._equipmentSimpleDropdownList.next(resp);
          this.equipmentDropdown.equipmentDropdownloading$.next(false);
        }),
        catchError((error) => {
          this.equipmentDropdown.equipmentDropdownloading$.next(false);
          return of(error);
        }),
      )
      .subscribe();
  }
}
